<!--
    Generic modal for close without saving warning - embarks thez logic of showing modal on route leave if the component has updates - in case of change other than route leave (side panel closing),  update the v-model in the parent and use the click:submit emit
    <CloseWarningModal
        v-model="..."                   # Mandatory - v-model with the opening state fo the modal
        has-updates=".."                # Optional - does the current feature have update, and needs displaying the modal for close warning on route leave - applied if props displayOnRouteLeave is true
        :display-on-route-leave="..."   # Optional - should the modal be displayed on route leave if it has updates

        Emits:
        - @update:modelValue    # Fired on change of opening state of the modal
        - @click:submit         # Fired on click on the primary CTA to leave page
    />
-->
<script setup lang="ts">
/* UTILS */
/* COMPONENTS */
import { ALButton, ALModal } from '@af-lib/vue-ui';
import { computed } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';

type Props = {
  modelValue: boolean;
  // The two below props are used if the modal needs to be displayed automatically on route leave - not possible on side pa
  hasUpdates?: boolean;
  displayOnRouteLeave?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  hasUpdates: false,
  displayOnRouteLeave: false
});
const emits = defineEmits(['update:modelValue', 'click:submit']);
const router = useRouter();

let forceRedirect = false;
let nextRoute;

const show = computed({
  get: () => props.modelValue,
  set: value => emits('update:modelValue', value)
});

// FUNCTIONS
function redirectToTarget() {
  forceRedirect = true;
  if (nextRoute) {
    router.push({ path: nextRoute.path });
  }
}

/* On submit, emit and redirect to the target route if any */
function submit() {
  redirectToTarget();
  emits('click:submit');
}

/* LIFECYCLES */
// On route change, show warning modal if needed
if (props.displayOnRouteLeave) {
  onBeforeRouteLeave(to => {
    if (props.hasUpdates && !forceRedirect) {
      emits('update:modelValue', true);
      nextRoute = to;
      return false;
    }
    return true;
  });
}
</script>
<template>
  <ALModal v-model="show" size="m" :title="$t('general.quit.title')" icon="warning">
    <template #content>{{ $t('general.quit.message') }}</template>
    <template #footer>
      <ALButton type="tertiary" :label="$t('general.quit.stay')" @click="emits('update:modelValue', false)" />
      <ALButton type="primary" :label="$t('general.quit.leave')" @click="submit" />
    </template>
  </ALModal>
</template>
